import { liveQuoteTradeApiConnector } from 'apiConnector/secondary-market/live/trade';
import { ofType } from 'redux-observable';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import {
  secondaryMarketLiveQuoteDurationEnded,
  secondaryMarketLiveQuoteExpired,
  secondaryMarketLiveQuoteFailed,
  secondaryMarketLiveQuoteReceived,
  secondaryMarketLiveQuoteRejected,
} from 'state/secondaryMarket/live/quote/liveQuote.action';
import type { EpicWithThunk } from 'state/state.common';
import { workspaceTabSetBusy } from 'state/workspaces/workspace.actions';
import type { HttpError } from 'utils/http/httpClient.model';
import { extractHttpErrorMessage, extractTimeout } from 'utils/http/httpClient.model';
import { getSecondaryMarketMessage$ } from 'utils/streaming/messages';

import { getTabRemoved$ } from '../utils';

export function liveQuoteRequestEpic(): EpicWithThunk {
  return (action$, _state$, { httpClient, getNow, streaming, scheduler }) => {
    const { requestQuote } = liveQuoteTradeApiConnector(httpClient);
    return action$.pipe(
      ofType('SECONDARY_MARKET_LIVE_QUOTE_REQUESTED'),
      filter(action => action.workspaceType === 'secondaryMarketQuotes'),
      mergeMap(({ request, tabId, workspaceType, isReprice }) => {
        request.isReprice = isReprice;
        return requestQuote(request).pipe(
          mergeMap(data => {
            const tabRepriced$ = action$.pipe(
              ofType('SECONDARY_MARKET_LIVE_QUOTE_REQUESTED'),
              filter(t => t.tabId === tabId),
            );
            const tabRemoved$ = getTabRemoved$(action$, tabId);
            const cancelled$ = action$.pipe(
              ofType('SECONDARY_MARKET_LIVE_QUOTE_CANCEL_REQUESTED'),
              filter(c => c.tabId === tabId && c.quoteId === data.quoteId),
            );

            const expired$ = getSecondaryMarketMessage$(streaming).pipe(
              filter(x => x.type === 'SecondaryMarketLiveQuoteExpiry' && x.quoteId === data.quoteId),
              takeUntil(merge(tabRemoved$, cancelled$, tabRepriced$)),
              map(({ quoteId }) => secondaryMarketLiveQuoteExpired(tabId, quoteId, request.productKey)),
            );

            const ended$ =
              data.quoteDurationInMilliseconds !== undefined && data.quoteDurationInMilliseconds !== null
                ? of(secondaryMarketLiveQuoteDurationEnded(tabId, data.quoteId!)).pipe(
                    delay(data.quoteDurationInMilliseconds, scheduler),
                    takeUntil(merge(tabRemoved$, expired$, cancelled$, tabRepriced$)),
                  )
                : EMPTY;

            return merge(
              of(
                secondaryMarketLiveQuoteReceived(tabId, data, getNow()),
                workspaceTabSetBusy(tabId, workspaceType, false),
              ),
              expired$,
              ended$,
            );
          }),
          catchError((error: HttpError) => {
            const message = extractHttpErrorMessage(error, { backend: 'domino' });
            if (isRejection(error) && message !== undefined) {
              return of(
                secondaryMarketLiveQuoteRejected(tabId, request.productKey, message),
                workspaceTabSetBusy(tabId, workspaceType, false),
              );
            }
            return of(
              secondaryMarketLiveQuoteFailed(tabId, request.productKey, message, extractTimeout(error)),
              workspaceTabSetBusy(tabId, workspaceType, false),
            );
          }),
        );
      }),
    );
  };
}

export function isRejection(httpError: HttpError): boolean {
  return httpError?.status === 400;
}
